import { useEffect } from 'react'
import { Link, NavLink, Outlet } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import { ChartBar, House, Star, Trophy, UserCircle } from '@phosphor-icons/react'
import { SPRING_SOFT } from '../../../shared/motion.ts'
import { useAuth } from '../auth/AuthContext'
import { useCountUp } from '../useCountUp'
import brandMark from '../../../shared/assets/round6-snowfold-atlas-mark-v1.png'

function displayName(user: {
  display_name?: string
  username: string
  first_name: string
  last_name: string
} | null): string {
  if (!user) return ''
  return (
    user.display_name ||
    user.username ||
    [user.first_name, user.last_name].filter(Boolean).join(' ') ||
    ''
  )
}

export function Layout() {
  const { t } = useTranslation()
  const { user, refreshBalance } = useAuth()
  const balance = useCountUp(user?.balance ?? 0)
  const name = displayName(user)
  const initial = (name.trim().charAt(0) || '?').toUpperCase()

  useEffect(() => {
    void refreshBalance()
  }, [refreshBalance])

  const tabs = [
    { to: '/', label: t('nav.home'), end: true, icon: House },
    { to: '/record', label: t('nav.record'), end: false, icon: ChartBar },
    { to: '/tasks', label: t('nav.tasks'), end: false, icon: Star },
    { to: '/leaderboard', label: t('nav.leaderboard'), end: false, icon: Trophy },
    { to: '/profile', label: t('nav.profile'), end: false, icon: UserCircle },
  ]

  return (
    <div className="app-shell">
      <header className="topbar">
        <div className="topbar-brand">
          <motion.img
            className="brand-mark"
            src={brandMark}
            alt=""
            initial={{ opacity: 0, rotate: -20, scale: 0.85 }}
            animate={{ opacity: 1, rotate: 0, scale: 1 }}
            transition={SPRING_SOFT}
          />
          <span className="brand-copy">
            <h1>ARCADE ATLAS</h1>
            <span>{t('app.subtitle')}</span>
          </span>
        </div>
        <Link
          to="/profile"
          className="topbar-user"
          aria-label={`${name || t('nav.profile')} · ${t('home.balance', { amount: Math.round(balance) })}`}
        >
          <span className="avatar avatar--sm" aria-hidden>
            {initial}
          </span>
          <span className="topbar-balance">
            <small>{t('home.balanceLabel')}</small>
            <strong>{Math.round(balance)}</strong>
          </span>
        </Link>
      </header>

      <motion.main
        className="content"
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
      >
        <Outlet />
      </motion.main>

      <nav className="tabbar" aria-label={t('nav.ariaLabel')}>
        {tabs.map((tab) => {
          const Icon = tab.icon
          return (
            <NavLink key={tab.to} to={tab.to} end={tab.end}>
              {({ isActive }) => (
                <>
                  {isActive && (
                    <motion.span
                      layoutId="tab-pill"
                      className="tab-pill"
                      transition={SPRING_SOFT}
                      aria-hidden
                    />
                  )}
                  <span className="tab-inner">
                    <Icon
                      className="nav-icon"
                      weight={isActive ? 'fill' : 'bold'}
                      aria-hidden
                    />
                    <span className="tab-label">{tab.label}</span>
                    {isActive ? <img className="tab-spark" src={brandMark} alt="" /> : null}
                  </span>
                </>
              )}
            </NavLink>
          )
        })}
      </nav>
    </div>
  )
}
